import { useState } from 'react';
import { Truck, Package, Search, ArrowRight } from 'lucide-react';
import BookTransportForm from '@/components/Logistics/BookTransportForm';
import LimitedTrackingMap from '@/components/Logistics/LimitedTrackingMap';

const LogisticsPortalPage = () => {
  const [activeTab, setActiveTab] = useState<'book' | 'track'>('book');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Truck size={48} />
            <div>
              <h1 className="text-4xl font-bold">Onion Trade Logistics</h1>
              <p className="text-blue-100 mt-2">Fast, Reliable Delivery Services</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl mb-2">🚛</div>
              <div className="font-semibold">Long Distance</div>
              <div className="text-sm text-blue-100">Heavy cargo trucking</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl mb-2">🏍️</div>
              <div className="font-semibold">Nairobi Local</div>
              <div className="text-sm text-blue-100">Fast urban delivery</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <div className="text-3xl mb-2">📍</div>
              <div className="font-semibold">Real-Time Tracking</div>
              <div className="text-sm text-blue-100">Know exactly where your cargo is</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 -mt-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('book')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'book'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Package size={20} />
              Book Transport
            </button>
            <button
              onClick={() => setActiveTab('track')}
              className={`flex-1 px-6 py-4 font-semibold transition-colors flex items-center justify-center gap-2 ${
                activeTab === 'track'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Search size={20} />
              Track Shipment
            </button>
          </div>

          <div className="p-6">
            {activeTab === 'book' ? (
              <div>
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Book Your Delivery</h2>
                  <p className="text-gray-600">
                    Choose your vehicle type, enter pickup and delivery locations, and we'll handle the rest.
                  </p>
                </div>
                <BookTransportForm />
              </div>
            ) : (
              <div>
                <div className="mb-6 text-center">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">Track Your Shipment</h2>
                  <p className="text-gray-600">
                    Enter your tracking code to see real-time updates on your delivery.
                  </p>
                </div>
                <LimitedTrackingMap />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">Why Choose Our Logistics?</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              <Truck className="text-blue-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Professional Drivers</h3>
            <p className="text-gray-600">
              Our experienced drivers ensure your cargo arrives safely and on time. All drivers are vetted and rated by customers.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              <Package className="text-green-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Cargo Protection</h3>
            <p className="text-gray-600">
              Your goods are our priority. We ensure proper handling and secure transport for all shipments, with insurance available.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              <Search className="text-purple-600" size={32} />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-3">Real-Time Updates</h3>
            <p className="text-gray-600">
              Track your shipment every step of the way with GPS tracking and automatic status notifications.
            </p>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Ship?</h2>
          <p className="text-xl mb-8 text-blue-100">
            Get started with reliable logistics services today. Book in minutes, track in real-time.
          </p>
          <button
            onClick={() => setActiveTab('book')}
            className="px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-colors inline-flex items-center gap-2 shadow-xl"
          >
            Book Transport Now
            <ArrowRight size={20} />
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-xl font-bold mb-4">Onion Trade Logistics</h3>
            <p className="text-gray-400">
              Reliable logistics and delivery services powered by advanced AI coordination.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/" className="hover:text-white">Home</a></li>
              <li><a href="/order" className="hover:text-white">Place Order</a></li>
              <li><a href="/track" className="hover:text-white">Track Order</a></li>
              <li><a href="/admin" className="hover:text-white">Staff Login</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact</h3>
            <ul className="space-y-2 text-gray-400">
              <li>📞 +254 700 000 000</li>
              <li>📧 logistics@oniontrade.co.ke</li>
              <li>📍 Nairobi, Kenya</li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-8 pt-8 border-t border-gray-800 text-center text-gray-400">
          <p>© 2026 Onion Trade ERP. Powered by Sal AI ✨</p>
        </div>
      </footer>
    </div>
  );
};

export default LogisticsPortalPage;
